import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { EnhancedTextArea } from 'v2/shared/components/enhanced-textarea'
import z from 'zod'
import { NodevarList } from '../../components/nodevar-list'
import type { v2validations } from '../../config/validation.config'
import { type AiAgentNodeformData, FlowNodeTypes } from '../../types'
import { getProviders } from '../../utils'
import { Actions } from './actions'
import { AIInstructions } from './ai-instructions'
import { NodeConfigs } from './node-configs'

export const aiAgentsFields = (
  validationSchema: ReturnType<typeof v2validations>[
    | FlowNodeTypes.ai_agent_chat
    | FlowNodeTypes.ai_agent_call
    | FlowNodeTypes.ai_agent_message
    | FlowNodeTypes.ai_agent_whatsapp],
  flowNodeType: FlowNodeTypes,
  nodeVars: string[],
): GenericFieldConfig<AiAgentNodeformData> => [
  {
    name: 'name',
    label: 'Name',
    zodValidation: validationSchema.shape.name,
    componentType: 'input',
    inputProps: {
      type: 'text',
    },
  },
  {
    name: 'conversation_type',
    label: 'Conversation type',
    zodValidation: validationSchema.shape.conversation_type,
    componentType: 'radioGroup',
    afterOnChange: (values: AiAgentNodeformData, _, form) => {
      if (values.channel === 'whatsapp') {
        form?.validateField('buttons', 'change')
      }
    },
    onChangeListenTo: ['channel'],
    visible: (values: AiAgentNodeformData) => values.channel !== 'call',
    inputProps: {
      options: [
        { label: 'AI generated', value: 'ai_generated' },
        { label: 'Predefined message', value: 'preset_message' },
      ],
    },
  },
  {
    name: 'model_config',
    label: 'Model',
    className: 'mt-2',
    componentType: 'selectWithGroups',
    onChangeListenTo: ['conversation_type', 'channel'],
    visible: (values: AiAgentNodeformData) => values.channel === 'call',
    valueFormatter: (value: { model: string; provider: string }) => value?.model,
    eventDataExtractor: (value: string) => {
      const provider = getProviders()?.llm_providers.find((p) =>
        p.models.some((m) => m.id === value),
      )
      return {
        model: value,
        provider: provider?.id,
      }
    },
    zodValidation: validationSchema.shape.model_config,
    getDynamicProps: () => {
      const providers = getProviders()
      return {
        inputProps: {
          placeholder: 'Select LLM model',
          groups: providers?.llm_providers.map((provider) => ({
            label: provider.label,
            options: provider.models.map((model) => ({
              label: model.label,
              value: model.id,
            })),
          })),
        },
      }
    },
  },
  {
    name: 'disable_message_type',
    label: '',
    componentType: 'switch',
    className: '-mb-4',
    inputProps: {
      className: 'absolute right-0',
    },
    onChangeListenTo: ['conversation_type'],
    visible: (values: AiAgentNodeformData) => values.conversation_type === 'ai_generated',
    zodValidation: validationSchema.shape.disable_message_type,
    valueFormatter: (value: boolean) => !value,
    eventDataExtractor: (e: boolean) => !e,
    afterOnChange: (values: AiAgentNodeformData, _, form) => {
      if (values.disable_message_type && form) {
        form.setFieldValue('message_type', 'NONE')
      }
      if (!values.disable_message_type && form) {
        form.setFieldValue('message_type', 'GENERATED')
      }
    },
  },
  {
    name: 'message_type',
    label: 'Greeting message type',
    componentType: 'select',
    className: '-mt-2',
    zodValidation: validationSchema.shape.message_type,
    onChangeListenTo: ['conversation_type', 'disable_message_type'],
    visible: (values: AiAgentNodeformData) => values.conversation_type === 'ai_generated',
    disabled: (values: AiAgentNodeformData) => !values.disable_message_type,
    getDynamicProps: (values: AiAgentNodeformData) => ({
      inputProps: {
        options: [
          { label: 'AI generated message', value: 'GENERATED' },
          { label: 'Static message', value: 'STATIC' },
        ],
        searchable: false,
        placeholder: !values.disable_message_type ? 'Select greeting message type' : 'None',
        disabled: values.disable_message_type,
      },
    }),
  },
  {
    name: 'wait_for_response',
    label: 'Wait for response',
    componentType: 'checkbox',
    visible: (values: AiAgentNodeformData) => {
      return values.channel !== 'call'
    },
    onChangeListenTo: ['channel'],
    zodValidation: validationSchema.shape.wait_for_response,
  },
  {
    name: 'reply_timeout',
    label: 'Reply timeout',
    componentType: 'inputNumber',
    visible: (values: AiAgentNodeformData) => {
      return values.wait_for_response === true && values.channel !== 'call'
    },
    zodValidation: validationSchema.shape.reply_timeout,
    onChangeListenTo: ['wait_for_response', 'channel'],
    inputProps: {
      min: 60,
      max: 3600,
    },
  },
  {
    name: 'message_prompt',
    label: 'Greeting instructions',
    inputProps: {
      placeholder:
        'Provide some details of your first message and AI will generate a personalised message for each customer.',
      endIcon: <NodevarList nodevars={nodeVars} inputElementSelector="#message_prompt .ql-ref" />,
    },
    component: ({ value, onChange, ...props }) => (
      <EnhancedTextArea
        value={value as string}
        onChange={onChange}
        showFileUpload={flowNodeType !== FlowNodeTypes.ai_agent_call}
        className="min-h-[60px]"
        {...props}
      />
    ),
    eventDataExtractor: (e: string) => e,
    type: 'custom',
    visible: (values: AiAgentNodeformData) =>
      values.message_type === 'GENERATED' &&
      !values.disable_message_type &&
      values.conversation_type === 'ai_generated',
    onChangeListenTo: ['message_type', 'disable_message_type', 'conversation_type'],
    zodValidation: (values: AiAgentNodeformData) => {
      if (values.message_type === 'GENERATED' && !values.disable_message_type) {
        return z.string().min(1, 'Prompt is required')
      }
      return z.string().nullable()
    },
  },
  {
    name: 'message_text',
    label: 'Greeting message',
    componentType: 'textarea',
    inputProps: {
      placeholder: 'Enter the first message here',
      endIcon: <NodevarList nodevars={nodeVars} inputElementSelector="#message_text" />,
    },
    visible: (values: AiAgentNodeformData) =>
      values.message_type === 'STATIC' &&
      !values.disable_message_type &&
      values.conversation_type === 'ai_generated',
    onChangeListenTo: ['message_type', 'disable_message_type', 'conversation_type'],
    zodValidation: (values: AiAgentNodeformData) => {
      if (values.message_type === 'STATIC' && !values.disable_message_type) {
        return z.string().min(1, 'Message is required')
      }
      return z.string().nullable()
    },
  },
  {
    name: 'tabs',
    type: 'basic',
    component: ({ form }) =>
      form.useStore((state) => state.values.conversation_type) === 'ai_generated' && (
        <Tabs defaultValue="instructions" className="col-span-2">
          <TabsList variant="default" className="mb-4 flex ">
            <TabsTrigger
              value="instructions"
              variant="default"
              className="flex-1 justify-center instructions"
            >
              AI instructions
            </TabsTrigger>
            <TabsTrigger
              value="actions"
              variant="default"
              className="flex-1 justify-center actions"
            >
              Actions
            </TabsTrigger>
            <TabsTrigger
              value="node_config"
              variant="default"
              className="flex-1 justify-center node-config"
            >
              Intent
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="instructions"
            forceMount
            className="data-[state=inactive]:hidden instructions"
          >
            <AIInstructions form={form} nodeVars={nodeVars} flowNodeType={flowNodeType} />
          </TabsContent>
          <TabsContent value="actions" forceMount className="data-[state=inactive]:hidden actions">
            <Actions form={form} nodeVars={nodeVars} />
          </TabsContent>
          <TabsContent
            value="node_config"
            forceMount
            className="data-[state=inactive]:hidden node-config"
          >
            <NodeConfigs form={form} />
          </TabsContent>
        </Tabs>
      ),
  },
]
